import { api } from './api';
import { 
  Procedure, 
  ProcedureData, 
  ProcedureFilters, 
  ProcedureStatistics, 
  ProcedureTimelineEntry,
  ProcedureTypeReference,
  OutcomeOption,
  ReinterventionOption
} from '@/types/procedure.types';
import { PaginatedResponse } from '@/types/api.types';

export const procedureService = {
  // Get all procedure types from API (dynamic, not hardcoded)
  getProcedureTypes: async (): Promise<ProcedureTypeReference[]> => {
    const response = await api.get<ProcedureTypeReference[]>('/api/reference/procedure-types');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca tipurile de proceduri');
  },

  // Get paginated list of procedures
  getProcedures: async (filters?: ProcedureFilters): Promise<PaginatedResponse<Procedure>> => {
    const response = await api.get<any>('/api/procedures', { 
      params: filters 
    });
    
    console.log('ProcedureService - Raw API response:', response);
    
    if (response.success) {
      // Map backend response structure to frontend expected structure
      const pagination = response.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      };
      
      return {
        data: response.data || [],
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        pages: pagination.pages
      };
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca procedurile');
  },

  // Get single procedure by ID
  getProcedure: async (id: number): Promise<Procedure> => {
    const response = await api.get<Procedure>(`/api/procedures/${id}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Nu s-a putut încărca procedura');
  },

  // Create new procedure
  createProcedure: async (data: ProcedureData): Promise<Procedure> => {
    console.log('Creating procedure with data:', data);
    const response = await api.post<any>('/api/procedures', data);
    
    if (response.success && response.data) {
      // Backend returns { message: string, procedure: Procedure }
      return response.data.procedure || response.data;
    }
    
    throw new Error(response.error || 'Nu s-a putut crea procedura');
  },

  // Update existing procedure
  updateProcedure: async (id: number, data: ProcedureData): Promise<Procedure> => {
    const response = await api.put<any>(`/api/procedures/${id}`, data);
    
    if (response.success && response.data) {
      // Backend returns { message: string, procedure: Procedure }
      return response.data.procedure || response.data;
    }
    
    throw new Error(response.error || 'Nu s-a putut actualiza procedura');
  },

  // Delete procedure
  deleteProcedure: async (id: number): Promise<void> => {
    const response = await api.delete(`/api/procedures/${id}`);
    
    if (!response.success) {
      throw new Error(response.error || 'Nu s-a putut șterge procedura');
    }
  },

  // Get all procedures for a specific patient
  getProceduresByPatient: async (patientId: number): Promise<Procedure[]> => {
    const response = await api.get<Procedure[]>(`/api/procedures/patient/${patientId}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca procedurile pacientului');
  },

  // Get procedure timeline for a patient (grouped by year)
  getProcedureTimeline: async (patientId: number): Promise<ProcedureTimelineEntry[]> => {
    const response = await api.get<ProcedureTimelineEntry[]>(`/api/procedures/patient/${patientId}/timeline`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Nu s-a putut încărca cronologia procedurilor');
  },

  // Get procedure statistics for a patient
  getProcedureStatistics: async (patientId: number): Promise<ProcedureStatistics> => {
    const response = await api.get<ProcedureStatistics>(`/api/procedures/patient/${patientId}/statistics`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca statisticile procedurilor');
  },

  // Get outcome options for dropdown
  getOutcomeOptions: async (): Promise<OutcomeOption[]> => {
    const response = await api.get<OutcomeOption[]>('/api/procedures/outcome-options');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca opțiunile de rezultat');
  },

  // Get reintervention options for dropdown
  getReinterventionOptions: async (): Promise<ReinterventionOption[]> => {
    const response = await api.get<ReinterventionOption[]>('/api/procedures/reintervention-options');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca opțiunile de reintervenție');
  }
};